import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Input } from 'components/ui/input';
import { BadgeAlert, LoaderIcon } from 'lucide-react';
import serviceLimitApis from 'features/bot-builder/services/serviceLimitApi';
import { useEffect, useState } from 'react';
import { serviceMappings } from 'features/bot-builder/constants';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useNavigate } from 'react-router-dom';

const AdvancedConfiguration = ({
  open,
  setOpen,
  services,
  botId,
  onBotCreated,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  services: {
    id: number;
    name: string;
    menu_name: string;
    menu_description: string;
    isActive: boolean;
  }[];
  botId: string;
  onBotCreated?: () => void;
}) => {
  const headings = ['Services', 'Reset period', 'Usage limit'];
  const navigate = useNavigate();
  const [input, setInput] = useState<{
    [key: number]: {
      usage_limit: number;
      reset_period: 'DAILY' | 'MONTHLY' | 'YEARLY';
    };
  }>({});
  const [isAdded, setIsAdded] = useState<{ [key: number]: boolean }>({});
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});

  const createLimit = async (id: number) => {
    if (!id) return;
    
    setLoading((prev) => ({ ...prev, [id]: true }));
    const serviceInput = {
      telegram_bot_id: botId,
      service_id: id,
      usage_limit: input[id]?.usage_limit || 1,
      reset_period: input[id]?.reset_period || 'DAILY',
    };

    try {
      const response = await serviceLimitApis.createLimit(serviceInput);
      if (response.success) {
        setIsAdded((prev) => ({
          ...prev,
          [id]: true,
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const allAdded =
    services.length > 0 &&
    Object.keys(isAdded).length === services.length &&
    Object.values(isAdded).every((val) => val);

  useEffect(() => {
    // Initialize input state for each service
    if (services && services.length > 0) {
      const initialInputs: {
        [key: number]: {
          usage_limit: number;
          reset_period: 'DAILY' | 'MONTHLY' | 'YEARLY';
        };
      } = {};
      
      services.forEach(service => {
        if (service && service.id) {
          initialInputs[service.id] = {
            usage_limit: 1,
            reset_period: 'DAILY'
          };
        }
      });
      
      setInput(initialInputs);
    }
  }, [services]);

  useEffect(() => {
    if (allAdded) {
      setOpen(false);
      navigate('/bot-builder/history');
      onBotCreated?.();
    }
  }, [allAdded, setOpen]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Overlay className="fixed inset-0 bg-black/50" />
      <Dialog.Content className="fixed z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-lg shadow-lg p-4 md:p-9 flex flex-col gap-4 md:gap-6 bg-background text-text-primary w-[95%] md:w-auto max-h-[90vh] overflow-y-auto">
        <Dialog.Title className="text-lg md:text-2xl text-left font-bold text-primary">
          Advanced configuration
        </Dialog.Title>

        <div className="space-y-3 w-full">
          {/* headings */}
          <div className="hidden md:grid md:grid-cols-4 grid-rows-1 gap-x-6">
            {headings.map((heading, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="text-[14px] font-normal">{heading}</span>
                {idx === 1 && (
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <BadgeAlert size={15} className="cursor-pointer" />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="w-[150px] p-[4px] flex justify-center text-center z-[100] rounded-[4px] h-[98px] text-[12px] bg-background font-normal"
                          sideOffset={5}
                        >
                          Defines how often the usage limit resets. Eg, if set
                          to &ldquo;weekly&ldquo; the usage count restarts every
                          week.
                          <Tooltip.Arrow className="text-primary" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                )}
                {idx === 2 && (
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <BadgeAlert size={15} className="cursor-pointer" />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="w-[150px] p-[4px] flex justify-center text-center z-[100] rounded-[4px] h-[98px] text-[11px] bg-background font-normal"
                          sideOffset={5}
                        >
                          Sets the max no. of times the bot can be used before
                          the reset period ends. Eg,a limit of 100 means 100
                          uses per period.
                          <Tooltip.Arrow className="text-primary" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                )}
              </div>
            ))}
          </div>
          {/* inputs */}
          <div className="grid gap-y-4 md:gap-y-3">
            {services && services.length > 0 ? 
              services.map((service, idx) => {
                // Skip rendering if service is invalid
                if (!service || !service.id) return null;
                
                // Add a safety check for the service mapping
                const serviceName = service?.name || '';
                let serviceCheck;
                
                try {
                  // First check if serviceName exists and is a valid index in serviceMappings
                  if (serviceName && typeof serviceName === 'string' && serviceMappings && serviceMappings[serviceName]) {
                    serviceCheck = serviceMappings[serviceName];
                  } else {
                    // Fallback to a default if not found in the mappings
                    serviceCheck = { label: serviceName || 'Unknown Service', icon: null };
                  }
                } catch (error) {
                  // Provide a safe fallback if any error occurs
                  console.error("Error accessing service mapping:", error);
                  serviceCheck = { label: 'Service', icon: null };
                }
                
                return (
                  <div
                    className="flex flex-col md:grid md:grid-flow-col md:grid-cols-4 gap-2 md:gap-x-6 items-start md:items-center"
                    key={`service-${service.id}-${idx}`}
                  >
                    <p className="text-[14px] font-bold">
                      {serviceCheck.label}
                    </p>
                    <div className="w-full">
                      <label className="block md:hidden text-xs mb-1">
                        Reset period
                      </label>
                      <Select
                        defaultValue="DAILY"
                        onValueChange={(value) =>
                          setInput((prev) => ({
                            ...prev,
                            [service.id]: {
                              ...prev[service.id],
                              reset_period: value as
                                | 'DAILY'
                                | 'MONTHLY'
                                | 'YEARLY',
                            },
                          }))
                        }
                      >
                        <SelectTrigger className="w-full bg-background-main border-2 rounded-md">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                          <SelectItem value="YEARLY">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full">
                      <label className="block md:hidden text-xs mb-1">
                        Usage limit
                      </label>
                      <Input
                        onChange={(e) =>
                          setInput((prev) => ({
                            ...prev,
                            [service.id]: {
                              ...prev[service.id],
                              usage_limit: Number(e.target.value),
                            },
                          }))
                        }
                        placeholder="Add limit"
                        className="w-full border-2 focus:outline-none focus:ring-0 placeholder:text-text-placeholder placeholder:font-normal placeholder:text-[16px] bg-background focus:border-primary"
                      />
                    </div>
                    <button
                      type="button"
                      title="add"
                      className="w-full md:w-auto h-8 rounded-md disabled:bg-input text-[14px] leading-7 flex items-center justify-center bg-secondary text-primary"
                      onClick={() => createLimit(service.id)}
                      disabled={isAdded[service.id]}
                    >
                      {loading[service.id] ? (
                        <LoaderIcon
                          size={20}
                          className="animate-spin text-white"
                        />
                      ) : isAdded[service.id] ? (
                        <span>Added</span>
                      ) : (
                        <span>Save</span>
                      )}
                    </button>
                  </div>
                );
              })
              : (
                <p>No services selected</p>
              )
            }
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-3">
          <p className="text-[11px] md:text-[12px] leading-[16px] font-light text-primary">
            NOTE: Save details for all services individually to go the next step
          </p>

          <div className="flex gap-3 w-full md:w-auto">
            <Button
              variant="customSecondary"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={!allAdded}
              className="text-[14px] font-normal flex-1 md:flex-none"
            >
              Cancel
            </Button>

            <Button
              variant="customPrimary"
              size="sm"
              className="text-[14px] font-normal flex-1 md:flex-none"
              type="button"
              disabled={!allAdded}
              onClick={() => setOpen(false)}
            >
              <span>Continue</span>
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AdvancedConfiguration;
